import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation, useTRPCQuery } from '../../../../trpc/trpc';
import type { ReviewQueue } from '../outreachTypes';
import { useInvalidateOutreach } from './useInvalidateOutreach';
import { useRetry } from './useRetry';

export interface PitchEdit {
  messageUuid: string;
  subject: string;
  body: string;
}

/**
 * The review queue of one campaign (frame f1) and its writes. Messages arrive as the job writes
 * them: `outreach.message.updated` reads the queue again.
 */
export function useReviewQueue(accountId: number, campaignUuid: string) {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const invalidate = useInvalidateOutreach();
  const query = useTRPCQuery(trpcAPI.outreach.review.queue.queryOptions({ accountId, campaignUuid }, { retry: false }));
  const retry = useRetry(query.refetch);
  const done = () => invalidate(['review', 'campaigns']);

  const approve = useTRPCMutation({
    mutationFn: (messageUuid: string) => trpcClient.outreach.review.approve.mutate({ accountId, messageUuid }),
    onSuccess: done,
  });
  const skip = useTRPCMutation({
    mutationFn: (messageUuid: string) => trpcClient.outreach.review.skip.mutate({ accountId, messageUuid }),
    onSuccess: done,
  });
  const edit = useTRPCMutation({
    mutationFn: (input: PitchEdit) => trpcClient.outreach.review.edit.mutate({ accountId, ...input }),
    onSuccess: done,
  });
  const approveRest = useTRPCMutation({
    mutationFn: () => trpcClient.outreach.review.approveRest.mutate({ accountId, campaignUuid }),
    onSuccess: done,
  });

  const busyUuid = approve.isPending
    ? approve.variables
    : skip.isPending
      ? skip.variables
      : edit.isPending
        ? edit.variables?.messageUuid
        : undefined;

  return {
    queue: query.data as ReviewQueue | undefined,
    isLoading: query.isLoading,
    error: query.error,
    retry,
    approve: approve.mutateAsync,
    skip: skip.mutateAsync,
    edit: edit.mutateAsync,
    approveRest: approveRest.mutateAsync,
    isApprovingRest: approveRest.isPending,
    busyUuid: busyUuid ?? null,
  };
}
